<template>
	<v-app v-if="isLoggedIn" style="height: 100vh">
		<template v-if="systemInputDevices && systemOutputDevices">
			<v-overlay :model-value="inactiveUser" class="align-center justify-center" transition="none">
				<GeneralConfirmation
					title="Inactivity Warning!"
					:message="inactivityMessage"
					:button-options="buttonOptions"
					@cancel="resetLogoutTimer"
				/>
			</v-overlay>
			<TopBar v-if="!isPopoutPanel" />
			<Main />
			<FullStoryInitializer v-if="environment === 'ZETTACLOUD'" />
		</template>
	</v-app>
	<v-app v-else>
		<HomePage />
	</v-app>
</template>

<script>
/* Modules */
import HomePage from "@/components/HomePage.vue";
import Main from "@/components/Main.vue";
import GeneralConfirmation from "@/components/dialogs/GeneralConfirmation.vue";
import TopBar from "@/components/TopBar.vue";

/* Mixins */
import SequencerPlayerStudioStatusMixin from "@/mixins/SequencerPlayerStudioStatus";

/* Plugins */
import config from "@/config";
import FullStoryInitializer from "@/plugins/FullStoryInitializer.vue";

export default {
	name: "App",

	components: {
		FullStoryInitializer,
		GeneralConfirmation,
		HomePage,
		TopBar,
		Main
	},

	mixins: [SequencerPlayerStudioStatusMixin],

	data() {
		return {
			inactiveTimer: 0,
			inactiveTimerTicker: null,
			logoutTimer: 30,
			logoutTicker: null,
			heartbeatWorker: null,
			buttonOptions: {
				agreeBtn: {
					enabled: false,
					message: "Yes"
				},
				cancelBtn: {
					enabled: true,
					message: "Cancel"
				}
			},
			isPopoutPanel: false
		};
	},

	computed: {
		isLoggedIn() {
			return this.$store.getters.getAccessToken() !== null;
		},

		inactiveUser() {
			// inactiveTimer in seconds
			return this.inactiveTimer >= 3600;
		},

		inactivityMessage() {
			return `It seems that you are not here. The system will log you out in ${this.logoutTimer} seconds.`;
		},

		systemInputDevices() {
			return this.$store.getters.systemInputDevices();
		},

		systemOutputDevices() {
			return this.$store.getters.systemOutputDevices();
		},

		environment() {
			return config.ORIGIN;
		}
	},

	watch: {
		async isLoggedIn(loggedIn) {
			// Popout windows (e.g. MidiUI) are separate browser windows/JS contexts.
			// They receive the state they need (studioSettings, sequencer snapshot, tokens, etc.)
			// from the main window through the sharedStore plugin (BroadcastChannel), so they
			// must NOT re-run the full app initialization here - doing so would, among other
			// things, open a brand new websocket/WebRTC connection instead of reusing the
			// existing one from the main window.
			if (loggedIn && !this.isPopoutPanel) {
				if (config.ORIGIN === "SOUNDPLUS") {
					// Full story: identify logged-in user
					const FSActivated = await this.$store
						.dispatch("getServiceFeatures")
						.then(res => {
							return res.data.some(f => {
								return (
									f.reference === "zettacloud_live_console_activate_full_story" &&
									f.category === "Global"
								);
							});
						})
						.catch(err => {
							console.log(err);
							this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
								message: "An error occurred when trying to load Service features."
							});
						});

					if (FSActivated) {
						import("@/plugins/FullStory");
					}
				}

				//Get System input/output audio devices
				this.$store.dispatch("getSystemDevices");
				navigator.mediaDevices.ondevicechange = () => {
					this.$store.dispatch("getSystemDevices");
				};

				this.$store.dispatch("openWebsocket");
				this.$store.dispatch("getUserObjects").catch(() => {
					this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
						message: "An error occurred when trying to load the user stations."
					});
				});
				this.$store.dispatch("getFavoriteStations");
				this.$store.dispatch("getAssetTypes").then(assetTypes => {
					let localStorageAssetTypes = JSON.parse(localStorage.getItem("assetTypes"));

					if (localStorageAssetTypes) {
						assetTypes.forEach(assetType => {
							const localAssetType = localStorageAssetTypes.find(
								localAssetType => localAssetType.AssetTypeID === assetType.AssetTypeID
							);
							assetType.LastAccessed = localAssetType?.LastAccessed ?? 0; // If asset has not accessed yet, set the "LastAccessed" as the start of time
						});
						this.$store.dispatch("updateAssetsLastAccessed", "ALL");
					} else {
						this.$store.dispatch("updateAssetsLastAccessed");
					}
				});
				this.$store.dispatch("setRecentlySearchedFromLocalStorage");
				this.$store.dispatch("getAccount").then(response => this.$store.commit("setAccount", response));
				this.$store.dispatch("getStudioSettings");
				this.$store.dispatch("coreMessageSubscription");

				window.addEventListener("keydown", this.resetLogoutTimer);
				window.addEventListener("click", this.resetLogoutTimer);
				this.stationConnectedToStudio ? this.resetLogoutTimer() : this.incrementLogoutTimer();

				window.addEventListener("beforeunload", ev => {
					if (this.userAnyRole) {
						sessionStorage.setItem(
							"liveState",
							JSON.stringify({
								studio: this.studioSettings.reference,
								station: this.$store.getters.liveModeSelectedStationID(),
								role: this.userRole,
								micOn: this.$store.getters.isMicOn(),
								midiIsOpen: this.$store.getters.getIsMidiOpen()
							})
						);
					} else {
						sessionStorage.removeItem("liveState");
					}

					if (this.userIsHost) {
						ev.preventDefault();
						ev.returnValue = "";
					}
				});
			}
		},

		stationConnectedToStudio(value) {
			value ? this.resetLogoutTimer() : this.incrementLogoutTimer();
		},

		inactiveUser(isInactive) {
			if (isInactive) {
				this.logoutTicker = this.$worker.setInterval(() => {
					this.logoutTimer--;
					if (this.logoutTimer === 0) {
						this.$worker.clearInterval(this.inactiveTimerTicker);
						this.$worker.clearInterval(this.logoutTicker);
						this.inactiveTimer = 0;
						this.$store.dispatch("signOut", config.ORIGIN === "ZETTACLOUD");
					}
				}, 1000);
			}
		}
	},

	beforeCreate() {
		setMetaTag({
			"http-equiv": "Content-Security-Policy",
			content: formatCSPSettings(config.CSP)
		});
	},

	created() {
		this.isPopoutPanel = !!new URLSearchParams(window.location.search).get("panel");
	},

	mounted() {
		// A popped-out panel's DOM (e.g. the MIDI panel's sliders) gets physically
		// moved into this window by dockview, but the JS that manages it - Vue,
		// Vuetify, etc. - keeps running in the MAIN window's realm, since it was never
		// reloaded here. Libraries that track mouse/touch drags via
		// window.addEventListener("mousemove"/"mouseup", ...) (e.g. Vuetify's
		// VSlider, used by the MIDI faders) register those listeners on the MAIN
		// window's `window`, so once a drag happens inside THIS window, those events
		// never reach them - the initial click registers (it's bound to the element
		// itself) but dragging never updates and mouseup never fires. Forward these
		// events to window.opener so drag-based interactions keep working here.
		if (this.isPopoutPanel && window.opener) {
			["mousemove", "mouseup", "touchmove", "touchend"].forEach(type => {
				window.addEventListener(type, e => window.opener.dispatchEvent(new e.constructor(e.type, e)), {
					passive: true
				});
			});
		}
	},

	beforeUnmount() {
		window.removeEventListener("keydown", this.resetLogoutTimer);
		window.removeEventListener("click", this.resetLogoutTimer);
	},

	methods: {
		incrementLogoutTimer() {
			if (!this.inactiveTimerTicker) {
				this.inactiveTimerTicker = this.$worker.setInterval(() => {
					this.inactiveTimer++;
				}, 1000);
			}
		},

		resetLogoutTimer() {
			if (this.inactiveTimerTicker) {
				this.$worker.clearInterval(this.inactiveTimerTicker);
				this.inactiveTimerTicker = null;
			}
			if (this.logoutTicker) {
				this.$worker.clearInterval(this.logoutTicker);
				this.logoutTicker = null;
			}
			this.inactiveTimer = 0;
			this.logoutTimer = 30;
			if (!this.stationConnectedToStudio) {
				this.incrementLogoutTimer();
			}
		}
	}
};

const formatCSPSettings = CSPSettings => {
	const jsonCSP = JSON.parse(CSPSettings);
	const content = [];
	Object.keys(jsonCSP).forEach(src => {
		content.push(`${src} ${jsonCSP[src].join(" ")}`);
	});
	return content.join("; ");
};

const setMetaTag = attributes => {
	let metaTag = document.querySelector(`meta[http-equiv="${attributes["http-equiv"]}"]`);
	if (!metaTag) {
		metaTag = document.createElement("meta");
		document.head.appendChild(metaTag);
	}
	Object.entries(attributes).forEach(([key, value]) => {
		metaTag.setAttribute(key, value);
	});
};
</script>
